import {
  useGetEnrollmentJointVerifyIdentityData,
  useSubmitEnrollmentJointVerifyIdentityData,
  ResidencyStatus,
  CountryCode,
} from '@egw/api'
import { Alert, AlertType, Button, GenericError, Input, ProgressBar, RadioGroup, Select, Tag } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { EyeIcon } from '@heroicons/react/20/solid'
import { UserIcon } from '@heroicons/react/16/solid'
import { useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { dobIsValid, formatDateForAPI } from '../../../utils/form-validation'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'

const log = new Logger('Enrollment - Joint - VerifyIdentity')

interface FormData {
  ssn: string
  dateOfBirth: string
  residencyStatus: ResidencyStatus
  citizenshipCountries: CountryCode
}

export const VerifyIdentityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentJointVerifyIdentityData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointVerifyIdentityData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint verify identity data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint verify identity form', submitError)
    }
  }, [submitError])

  // Type guard for valid data response
  const isValidData = (
    responseData: unknown,
  ): responseData is {
    isError: boolean
    activePerson: {
      path?: string
      progress?: number
      dateOfBirth?: string
      residencyStatus?: string
      citizenshipCountries?: string[]
      name?: {
        firstName?: string
      }
    }
  } => {
    return responseData !== null && typeof responseData === 'object' && 'activePerson' in responseData
  }

  // Type guard for submit response
  const isValidSubmitResponse = (
    response: unknown,
  ): response is { isError: boolean; activePerson: { path?: string } } => {
    return response !== null && typeof response === 'object' && 'activePerson' in response
  }

  const activePerson = useMemo(() => {
    if (isValidData(data) && !data.isError) {
      return data.activePerson
    }
    return undefined
  }, [data])

  // Protect route
  useValidateRoute(activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - VerifyIdentity')

  const progress = activePerson?.progress
  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const _dateOfBirth = activePerson?.dateOfBirth ?? ''
  const _residencyStatus = activePerson?.residencyStatus ?? ''
  const _citizenshipCountries = useMemo(
    () => activePerson?.citizenshipCountries ?? [],
    [activePerson?.citizenshipCountries],
  )
  const applicationId = data && !data.isError ? data.activePerson.applicationId : ''

  const [isHide, setIsHide] = useState(true)
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isValid, errors: formErrors, touchedFields },
  } = useForm({
    defaultValues: {
      ssn: '',
      dateOfBirth: '',
      residencyStatus: '',
      citizenshipCountries: '',
    },
    mode: 'onChange',
  })

  const ssn = watch('ssn')
  const residencyStatus = watch('residencyStatus')
  const showCitizenshipCountries =
    residencyStatus === 'US_PERMANENT_RESIDENT' || residencyStatus === 'US_TEMPORARY_RESIDENT'

  useEffect(() => {
    if (!hasShownIntent && ssn && residencyStatus) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_IDENTITY_INTENT, { applicationId })
    }
  }, [hasShownIntent, ssn, residencyStatus])

  // Update form values when data is loaded
  useEffect(() => {
    if (activePerson && _dateOfBirth && typeof _dateOfBirth === 'string') {
      const dateParts = _dateOfBirth.split('-')
      const formattedDate = dateParts.length === 3 ? `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}` : ''

      reset({
        dateOfBirth: formattedDate,
        residencyStatus: _residencyStatus || '',
        citizenshipCountries: _citizenshipCountries[0] || '',
      })
    }
  }, [activePerson, reset, _dateOfBirth, _residencyStatus, _citizenshipCountries])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          activePerson: {
            taxId: formData.ssn,
            dateOfBirth: formatDateForAPI(formData.dateOfBirth),
            residencyStatus: formData.residencyStatus as string,
            ...(String(formData.citizenshipCountries) !== '' && {
              citizenshipCountries: [formData.citizenshipCountries as string],
            }),
          },
        })

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        sendEvent('bankjoint_enroll_identity_intent', { applicationId })
        // If path exists and is different
        if (isValidSubmitResponse(response) && !response.isError) {
          const path = response.activePerson.path
          if (path && newPathIsDifferent(location.pathname, path)) {
            sendEvent('user_enroll_identity_verified', { applicationId })
            void navigate(path)
          } else if (!path) {
            // If no path from BE, redirect to error
            log.error('No path returned from backend, redirecting to error page')
            void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
          }
        } else {
          // If no valid response, redirect to error
          log.error('Invalid response from backend, redirecting to error page')
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting verify identity form', e)
      }
    }
  })

  const residencyStatusOptions = useMemo(() => {
    return Object.keys(ResidencyStatus).map((key) => ({
      value: key,
      label: t(`joint.verify-identity.citizen_enum.${key}`),
    }))
  }, [t])

  const citizenshipCountriesOptions = useMemo(() => {
    return Object.keys(CountryCode).map((key) => ({
      value: key,
      label: t(`common.country_code.${key}`),
    }))
  }, [t])

  return (
    <EnrollmentJointLayout title={t('joint.verify-identity.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            className="mb-xs"
            title={t('joint.verify-identity.title')}
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.HOME_ADDRESS.path}`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />

          <p className="typo-primary-base text-text-meta mb-xl">{t('joint.verify-identity.description')}</p>

          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            {/* Date of Birth */}
            <p className="typo-primary-base-semibold mb-2xs">{t('joint.verify-identity.date_of_birth')}</p>
            <p className="typo-primary-base text-text-meta mb-sm">
              {t('joint.verify-identity.date_of_birth_description')}
            </p>
            <Controller
              name="dateOfBirth"
              control={control}
              rules={{
                required: t('joint.verify-identity.dob_error'),
                validate: (e) => dobIsValid(e || '') || t('joint.verify-identity.dob_error'),
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Input
                  {...field}
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, '')
                    let formatted = ''

                    if (input.length > 0) {
                      formatted = input.slice(0, 2)
                      if (input.length > 2) {
                        formatted += `/${input.slice(2, 4)}`
                        if (input.length > 4) {
                          formatted += `/${input.slice(4, 8)}`
                        }
                      }
                    }

                    if (formatted.length <= 10) {
                      onChange(formatted)
                    }
                  }}
                  label={t('hycs.personal_info.dob_label')}
                  required
                  error={touchedFields.dateOfBirth ? formErrors.dateOfBirth?.message : undefined}
                  type="text"
                  className="mb-xl"
                  testId={getTestId('dateOfBirth', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />

            {/* SSN */}
            <p className="typo-primary-base-semibold mb-sm">{t('joint.verify-identity.ssn')}</p>
            <Controller
              name="ssn"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value: /^[0-9]{9}$/,
                  message: t('common.invalid_ssn'),
                },
              }}
              render={({ field: { ...restField } }) => (
                <Input
                  {...restField}
                  label={t('joint.verify-identity.itin')}
                  maxLength={9}
                  inputMode="numeric"
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '')
                    restField.onChange(newValue)
                  }}
                  icon={
                    <EyeIcon
                      onClick={() => {
                        setIsHide(!isHide)
                      }}
                      data-testid="ssn_input_icon"
                      className="h-5 w-5 cursor-pointer pointer-events-auto"
                    />
                  }
                  required
                  type={isHide ? 'password' : 'text'}
                  testId="ssn_input"
                  className="mb-xl"
                  error={touchedFields.ssn ? formErrors.ssn?.message : undefined}
                  isPII
                />
              )}
            />

            <p className="typo-primary-base-semibold mb-sm">{t('joint.verify-identity.citizen')}</p>
            <Controller
              name="residencyStatus"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref: _ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value || ''}
                  options={residencyStatusOptions}
                  name="residencyStatus"
                  testId="residencyStatus_checkbox"
                  className="!py-xs"
                />
              )}
            />

            {showCitizenshipCountries ? (
              <>
                <p className="typo-primary-base-semibold mb-sm mt-xl">
                  {t('joint.verify-identity.country_of_citizenship')}
                </p>

                <Controller
                  name="citizenshipCountries"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={citizenshipCountriesOptions}
                      label={t('joint.verify-identity.country_of_citizenship_label')}
                      required
                      error={touchedFields.citizenshipCountries ? formErrors.citizenshipCountries?.message : undefined}
                      className="w-full"
                      testId="citizenshipCountries_input"
                    />
                  )}
                />
              </>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
